import { createBrowserRouter, redirect } from 'react-router-dom'
import App from './pages/App'
import Login from './pages/Login/Login'
import DashboardLayout from './pages/Dashboard/Dashboard'
import PasswordReset from './pages/PasswordReset/PasswordReset'
import NewPassword from './pages/NewPassword/NewPassword'
import NotFoundPage from './components/NotFoundPage/NotFoundPage'
import PrivateLayout from './components/layout/Private'
import Template from './components/layout/Template'
import Profile from './components/Dashboard/Profile/Profile'
import Audit from './components/Dashboard/Audit/Audit'
import ServerHealth from './components/Dashboard/ServerHealth/ServerHealth'
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary'
import Roles from './components/Dashboard/Setting/Roles/Roles'
import Analytics from './components/Dashboard/Analytics/Analytics'
import Dashboard from './components/Dashboard/Dashboard/Dashboard'
import School from './components/Dashboard/Setting/School/School'
import Users from './components/Dashboard/Setting/Users/Users'
import Curriculum from './components/Dashboard/Setting/Curriculum/Curriculum'
import LearningContinuum from './components/Dashboard/Assessment/LearningContinuum/LearningContinuum'
import DevelopmentalRubric from './components/Dashboard/Assessment/DevelopmentalRubric/DevelopmentalRubric'
import Classes from './components/Dashboard/Assessment/Classes/Classes'
import Subject from './components/Dashboard/Assessment/Subject/Subject'
// Periodical report
import PeriodicalReport from './components/Dashboard/Assessment/PeriodicalReport/PeriodicalReport'
//Data entry
import TemplateBuilder from './components/Dashboard/DataEntry/Templates/Generator/TemplateBuilder/TemplateBuilder'
import AssessmentTemplates from './components/Dashboard/DataEntry/Templates/Assessments/AssessmentTemplates'
import SurveyTemplates from './components/Dashboard/DataEntry/Templates/Surveys/SurveyTemplates'
import AssessmentsList from './components/Dashboard/DataEntry/Assessments/AssessmentsList'
import AssessmentBuilder from './components/Dashboard/DataEntry/Assessments/AssessmentBuilder/AssessmentBuilder'
import AssessmentsMarking from './components/Dashboard/DataEntry/Assessments/Marking/AssessmentsMarking'
import SurveysList from './components/Dashboard/DataEntry/Surveys/SurveysList/SurveysList'
import SurveyPreview from './components/Dashboard/DataEntry/Surveys/SurveyPreview/SurveyPreview'
import MySurveysList from './components/Dashboard/DataEntry/MySurveys/MySurveysList'
import MyAssessmentsList from './components/Dashboard/DataEntry/MyAssessments/MyAssessmentsList'
import FillASurvey from './components/Dashboard/DataEntry/MySurveys/FillASurvey/FillASurvey'
import PhotoManagement from './components/Dashboard/Setting/PhotoManagement/PhotoManagement'
import Maintenance from './components/Dashboard/Setting/Maintenance/Maintenance'
import SurveyGlobal from './components/Dashboard/DataEntry/Surveys/SurveyGlobal/SurveyGlobal'
import AboutMeSurveyList from './components/Dashboard/DataEntry/MySurveys/AboutMeSurveyList/AboutMeSurveyList'
import FillASurveyAbout from './components/Dashboard/DataEntry/MySurveys/FillASurveyAbout/FillASurveyAbout'
import AboutMyClassesSurveyList from './components/Dashboard/DataEntry/MySurveys/AboutMyClassesSurveyList/AboutMyClassesSurveyList'
// Journal module (MF-67)
import JournalEntriesList from './components/Dashboard/Journal/Entries/EntriesList'
import JournalEntryForm from './components/Dashboard/Journal/Entries/EntryForm'
import JournalConfiguration from './components/Dashboard/Journal/Configuration/JournalConfiguration'
import Feed from './components/Dashboard/Feed/Feed'
import AnalyticsCurriculum from './components/Dashboard/Analytics/InternalLink/AnalyticsCurriculum/AnalyticsCurriculum'

const protectedRoute = () => {
  const accessToken = localStorage.getItem('accessToken')
  const userSession = localStorage.getItem('userSession')
  if (!accessToken || !userSession) {
    return false
  }

  return true
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />
  },
  {
    path: '/login',
    element: <Login />,
    loader: () => {
      const isValidSession = protectedRoute()

      if (isValidSession) {
        return redirect('/dashboard')
      }

      return {}
    },
    errorElement: (
      <Template>
        <ErrorBoundary />
      </Template>
    )
  },
  {
    // Lifted blog splat route
    path: '/dashboard/*',
    element: <DashboardLayout />,
    children: [
      // New dashboard settings route
      { index: true, Component: Dashboard },
      // Profile route
      { path: 'profile', element: <Profile /> },
      // Feed route
      { path: 'feed', element: <Feed /> },
      // Audit route
      { path: 'audit/*', Component: Audit },
      // MF-583 — server health (Maestro role only; the page and the API both check it)
      { path: 'server-health', Component: ServerHealth },
      // Roles route
      { path: 'roles', element: <Roles /> },
      // Analytics route
      { path: 'analytics/:code/analytics-curriculum/*', Component: AnalyticsCurriculum },
      { path: 'analytics/*', Component: Analytics },
      // School route
      { path: 'school/*', Component: School },
      // Curriculum route
      { path: 'curriculum/*', Component: Curriculum },
      // Developmental rubrics route
      { path: 'classes/*', Component: Classes },
      // Developmental rubrics route
      { path: 'developmental-rubrics/*', Component: DevelopmentalRubric },
      // Learning continuum route
      { path: 'learning-continuum/*', Component: LearningContinuum },
      // Photo management route
      { path: 'photo-management/*', Component: PhotoManagement },
      // Periodical report route
      { path: 'periodical-report/*', Component: PeriodicalReport },
      // Subjects route
      { path: 'subjects/*', Component: Subject },
      // Users route
      { path: 'users/*', Component: Users },
      // Maintenance route
      { path: 'maintenance', element: <Maintenance /> },
      // Parent dashboard route
      { path: ':studentId/*', Component: Dashboard },

      //Data Entry routes
      {
        path: 'data-entry/generator/assessment-templates/add',
        element: <TemplateBuilder type='assessment' mode='adding' />
      },
      {
        path: 'data-entry/generator/assessment-templates/edit/:id',
        element: <TemplateBuilder type='assessment' mode='editing' />
      },
      {
        path: 'data-entry/generator/assessment-templates',
        element: <AssessmentTemplates />
      },
      {
        path: 'data-entry/assessment-marking/:assessmentId',
        element: <AssessmentsMarking />
      },
      {
        path: 'data-entry/assessments/add/:type',
        element: <AssessmentBuilder mode='adding' />
      },
      {
        path: 'data-entry/assessments/:assessmentId/edit/:type',
        element: <AssessmentBuilder mode='editing' />
      },
      {
        path: 'data-entry/assessments',
        element: <AssessmentsList />
      },
      {
        path: 'data-entry/generator/survey-templates/add',
        element: <TemplateBuilder type='survey' mode='adding' />
      },
      {
        path: 'data-entry/generator/survey-templates/edit/:id',
        element: <TemplateBuilder type='survey' mode='editing' />
      },
      {
        path: 'data-entry/generator/survey-templates',
        element: <SurveyTemplates />
      },
      {
        path: 'data-entry/surveys',
        element: <SurveysList />
      },
      {
        path: 'data-entry/surveys/add/:type',
        element: <SurveyGlobal />
      },
      {
        path: 'data-entry/surveys/:surveyId/edit',
        element: <SurveyGlobal mode='editing' />
      },
      {
        path: 'data-entry/surveys/:surveyId/preview',
        element: <SurveyPreview />
      },
      {
        path: 'data-entry/my-surveys',
        element: <MySurveysList />
      },
      {
        path: ':id/data-entry/my-surveys',
        element: <MySurveysList />
      },
      {
        path: 'data-entry/my-surveys/about-me',
        element: <AboutMeSurveyList />
      },
      {
        path: ':id/data-entry/my-surveys/about-me',
        element: <AboutMeSurveyList />
      },
      {
        path: 'data-entry/my-surveys/about-my-classes',
        element: <AboutMyClassesSurveyList />
      },
      {
        path: ':id/data-entry/my-surveys/about-my-classes',
        element: <AboutMyClassesSurveyList />
      },
      {
        path: 'data-entry/my-assessments',
        element: <MyAssessmentsList />
      },
      {
        path: 'data-entry/my-surveys/:surveyId/fill-a-survey',
        element: <FillASurvey />
      },
      {
        path: 'data-entry/my-surveys/:surveyId/fill-a-survey-about/:recordId',
        element: <FillASurveyAbout />
      },

      // Journal module (MF-67)
      {
        path: 'journal/entries',
        element: <JournalEntriesList />
      },
      {
        path: 'journal/entries/add',
        element: <JournalEntryForm />
      },
      {
        path: 'journal/entries/edit/:id',
        element: <JournalEntryForm />
      },
      // Journal configuration: tabbed page (severities → ratings → zones → categories → templates)
      {
        path: 'journal/configuration/*',
        element: <JournalConfiguration />
      },

      // Not found route
      { path: '*', element: <NotFoundPage /> }
    ],
    loader: () => {
      const isValidSession = protectedRoute()
      const location = window.location.pathname

      if (!isValidSession) {
        let url = '/login'
        if (!['/login', '/password-reset', '/accept-invite', '/', '/dashboard'].includes(location)) {
          window.localStorage.setItem('redirect', location)
        }
        return redirect(url)
      }

      return {}
    },
    errorElement: (
      <PrivateLayout>
        <ErrorBoundary />
      </PrivateLayout>
    )
  },
  {
    path: '/password-reset',
    element: <PasswordReset />,
    loader: () => {
      const isValidSession = protectedRoute()

      if (isValidSession) {
        return redirect('/dashboard')
      }

      return {}
    },
    errorElement: (
      <Template>
        <ErrorBoundary />
      </Template>
    )
  },
  {
    path: '/password-reset/new/:token',
    element: <NewPassword title='Reset your password' type='update' />,
    loader: () => {
      const isValidSession = protectedRoute()

      if (isValidSession) {
        return redirect('/dashboard')
      }

      return {}
    },
    errorElement: (
      <Template>
        <ErrorBoundary />
      </Template>
    )
  },
  {
    path: '/accept-invite/:token',
    element: <NewPassword title='Activate your account' type='activate' />,
    loader: () => {
      const isValidSession = protectedRoute()

      if (isValidSession) {
        return redirect('/dashboard')
      }

      return {}
    },
    errorElement: (
      <Template>
        <ErrorBoundary />
      </Template>
    )
  },
  {
    path: '/error',
    element: (
      <Template>
        <ErrorBoundary />
      </Template>
    )
  },
  {
    path: '*',
    element: (
      <Template>
        <NotFoundPage showLink={true} />
      </Template>
    )
  }
])
